import { createRoutine } from 'redux-saga-routines'
import {
  fork,
  put,
  take,
  takeLatest,
  call,
  spawn,
  select
} from '@redux-saga/core/effects'
import { eventChannel } from 'redux-saga'
import qs from 'qs'
import { omit } from 'ramda'
import NotificationsService from 'src/services/NotificationsService'
import LaravelEchoService from 'src/services/LaravelEchoService'
import { showToast } from 'src/components/Toast'
import { normalizeNotificationsList } from './normalizers'
import { NOTIFICATION_EVENT_TYPES } from './consts'
import { selectNotificationsDetails } from './selectors'
import { toCamelCaseDeep } from 'src/utils/helpers'
import { path } from 'ramda'
import { isFalsy } from 'ramda-adjunct'
import moment from 'moment'

export const NOTIFICATION_EVENT_USER_NOTIFICATION_SENT =
  'NOTIFICATION_EVENT_USER_NOTIFICATION_SENT'

export const setNotificationsListOpenedRoutine = createRoutine(
  'SET_NOTIFICATIONS_LIST_OPENED'
)
export const getNotificationsListRoutine = createRoutine(
  'GET_NOTIFICATIONS_LIST'
)
export const getStickyNotificationsListRoutine = createRoutine(
  'GET_STICKY_NOTIFICATIONS_LIST'
)
export const getUnseenNotificationsCountRoutine = createRoutine(
  'GET_UNSEEN_NOTIFICATIONS_COUNT'
)
export const markAllNotificationsAsReadRoutine = createRoutine(
  'MARK_ALL_NOTIFICATIONS_AS_READ'
)

export const markNotificationAsReadRoutine = createRoutine(
  'MARK_NOTIFICATION_AS_READ'
)

export const markAllNotificationsAsSeenRoutine = createRoutine(
  'MARK_ALL_NOTIFICATIONS_AS_SEEN'
)
export const clearNotificationsRoutine = createRoutine('CLEAR_NOTIFICATIONS')

export const getNotificationsSettingsRoutine = createRoutine(
  'GET_NOTIFICATIONS_SETTINGS'
)

export const updateNotificationSettingRoutine = createRoutine(
  'UPDATE_NOTIFICATION_SETTING'
)

//actions

function* setNotificationsListOpened({ payload }) {
  yield put(setNotificationsListOpenedRoutine.success(payload))
}

function* getNotificationsList({ payload }) {
  yield put(getNotificationsListRoutine.request())
  try {
    const notifications = yield select(selectNotificationsDetails)
    const pageToFetch = payload.refresh ? 1 : payload.page || notifications.page

    const query = qs.stringify(
      {
        page: pageToFetch,
        limit: notifications.limit
      },
      { skipNull: true }
    )

    if (!notifications.allFetched || payload.refresh) {
      const response = yield call(
        NotificationsService.getNotificationsList,
        query
      )

      const isFirstLoad =
        notifications.page === 1 && notifications.notifications.length === 0

      yield put(
        getNotificationsListRoutine.success({
          data: response.data,
          meta: response.response.meta,
          append: payload.append,
          refresh: payload.refresh,
          firstLoad: isFirstLoad
        })
      )
    }
    yield put(getNotificationsListRoutine.fulfill())
  } catch (e) {
    console.log(e)
    yield put(getNotificationsListRoutine.failure(e))
  }
}

function* getStickyNotificationsList() {
  yield put(getStickyNotificationsListRoutine.request())
  try {
    const query = qs.stringify(
      {
        page: 1,
        limit: 20,
        filter: {
          only_sticky: true
        }
      },
      { skipNull: true }
    )

    const response = yield call(
      NotificationsService.getNotificationsList,
      query
    )

    yield put(getStickyNotificationsListRoutine.success(response.data))
  } catch (e) {
    console.log(e)
    yield put(getStickyNotificationsListRoutine.failure(e))
  } finally {
    yield put(getStickyNotificationsListRoutine.fulfill())
  }
}

function listenNotificationsEvents(userId) {
  return eventChannel(emitter => {
    LaravelEchoService.getInstance().listenToNotificationEvents(
      userId,
      (type, payload) => {
        switch (type) {
          case NOTIFICATION_EVENT_TYPES.NOTIFICATION_SENT:
            return emitter({
              type: 'NOTIFICATION_EVENT_USER_NOTIFICATION_SENT',
              payload
            })
          default:
            return null
        }
      }
    )
    return () => {}
  })
}

export function* getUnseenNotificationsCount() {
  try {
    const { data } = yield call(
      NotificationsService.getUnseenNotificationsCount
    )
    yield put(getUnseenNotificationsCountRoutine.success(data))
  } catch (e) {
    console.log(e)
  }
}

export function* markAllNotificationsAsRead() {
  try {
    yield call(NotificationsService.markAllNotificationsAsRead)
    yield spawn(getUnseenNotificationsCount)
    yield spawn(getNotificationsList, { payload: { page: 1 } })
  } catch (e) {
    console.log(e)
  }
}

export function* markNotificationAsRead({ payload }) {
  try {
    yield call(NotificationsService.markNotificationAsRead, payload.id)
    yield put(markNotificationAsReadRoutine.success({ id: payload.id }))
  } catch (e) {
    console.log(e)
  }
}

export function* markAllNotificationsAsSeen() {
  try {
    yield call(NotificationsService.markAllNotificationsAsSeen)
    yield spawn(getUnseenNotificationsCount)
  } catch (e) {
    console.log(e)
  }
}

export function* getNotificationsSettings() {
  yield put(getNotificationsSettingsRoutine.request())
  try {
    const { data } = yield call(NotificationsService.getNotificationsSettings)
    yield put(getNotificationsSettingsRoutine.success({ data }))
  } catch (e) {
    yield put(getNotificationsSettingsRoutine.failure())
    console.log(e)
  }
}

export function* clearNotifications() {
  yield put(clearNotificationsRoutine.success())
}

export function* updateNotificationSetting({ payload }) {
  yield put(updateNotificationSettingRoutine.request(payload))
  try {
    yield call(NotificationsService.updateNotificationSettings, [
      omit(['label'], payload.toJS())
    ])
    yield call(getNotificationsSettings)
  } catch (e) {
    console.log(e)
  }
}

function* showToastNotification({ payload }) {
  if (isFalsy(payload)) {
    return
  }

  const fallbackCreatedAt = moment().format()
  const rawCreatedAt =
    path(['created_at'], payload) ||
    path(['createdAt'], payload) ||
    fallbackCreatedAt

  const normalizedPayload = toCamelCaseDeep({
    createdAt: rawCreatedAt,
    ...payload
  })

  const normalizedNotification = normalizeNotificationsList([normalizedPayload])
  yield call(showToast, normalizedNotification[0])
}

//watchers

export function* listenNotificationsEventsWatcher(userId) {
  const channel = yield call(listenNotificationsEvents, userId)
  while (true) {
    const action = yield take(channel)
    yield spawn(getUnseenNotificationsCount)
    yield spawn(showToastNotification, action)
    yield spawn(getNotificationsList, { payload: { page: 1, refresh: true } })
  }
}

function* setNotificationsListOpenedRoutineWatcher() {
  yield takeLatest(
    setNotificationsListOpenedRoutine.TRIGGER,
    setNotificationsListOpened
  )
}

function* getNotificationsListRoutineWatcher() {
  yield takeLatest(getNotificationsListRoutine.TRIGGER, getNotificationsList)
}

function* getStickyNotificationsListRoutineWatcher() {
  yield takeLatest(
    getStickyNotificationsListRoutine.TRIGGER,
    getStickyNotificationsList
  )
}

function* getUnseenNotificationsCountRoutineWatcher() {
  yield takeLatest(
    getUnseenNotificationsCountRoutine.TRIGGER,
    getUnseenNotificationsCount
  )
}

function* markAllNotificationsAsReadRoutineWatcher() {
  yield takeLatest(
    markAllNotificationsAsReadRoutine.TRIGGER,
    markAllNotificationsAsRead
  )
}

function* markAllNotificationsAsSeenRoutineWatcher() {
  yield takeLatest(
    markAllNotificationsAsSeenRoutine.TRIGGER,
    markAllNotificationsAsSeen
  )
}

function* clearNotificationsRoutineWatcher() {
  yield takeLatest(clearNotificationsRoutine.TRIGGER, clearNotifications)
}

function* markNotificationAsReadRoutineWatcher() {
  yield takeLatest(
    markNotificationAsReadRoutine.TRIGGER,
    markNotificationAsRead
  )
}

function* getNotificationsSettingsRoutineWatcher() {
  yield takeLatest(
    getNotificationsSettingsRoutine.TRIGGER,
    getNotificationsSettings
  )
}

function* updateNotificationSettingRoutineWatcher() {
  yield takeLatest(
    updateNotificationSettingRoutine.TRIGGER,
    updateNotificationSetting
  )
}

function* showToastNotificationWatcher() {
  yield takeLatest(
    NOTIFICATION_EVENT_USER_NOTIFICATION_SENT,
    showToastNotification
  )
}

export const notificationsSagas = [
  fork(showToastNotificationWatcher),
  fork(setNotificationsListOpenedRoutineWatcher),
  fork(getNotificationsListRoutineWatcher),
  fork(getUnseenNotificationsCountRoutineWatcher),
  fork(markAllNotificationsAsReadRoutineWatcher),
  fork(markAllNotificationsAsSeenRoutineWatcher),
  fork(clearNotificationsRoutineWatcher),
  fork(markNotificationAsReadRoutineWatcher),
  fork(getNotificationsSettingsRoutineWatcher),
  fork(updateNotificationSettingRoutineWatcher),
  fork(getStickyNotificationsListRoutineWatcher)
]
